import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from "@mui/material"
import { TFaturaFechamentoData } from "../../../../../../../types/api/financeiro/detalhar-fechamentos-conta-corrente"
import { Controller, FormProvider, useForm } from "react-hook-form"
import { enqueueSnackbar } from "notistack";
import { formatarTelefone } from "../../../../../../../helpers/masks";
import { desformatarTelefone } from "../../../../../../../helpers/unmask";
import { useMutation, useQuery, useQueryClient } from "react-query";
import axiosClient from "../../../../../../../lib/axios";
import { useMemo } from "react";

export default function DialogEnviarFaturas({
    id,
    open,
    faturas,
    handleClose
}: DialogEnviarFaturasProps) {

    const queryClient = useQueryClient();

    //Estado inicial do React Hook Form
    const methods = useForm<{ faturas: TEnviarFaturas[] }>({
        defaultValues: {
            faturas: faturas.map(fatura => ({
                ...fatura,
                email: fatura.email,
                telefone: fatura.telefone ? formatarTelefone(fatura.telefone) : undefined,
            }))
        }
    });
    const { control, handleSubmit, setError } = methods

    //Querys e mutations
    const { data } = useQuery({
        queryKey: ["configuracoes-whatsapp"],
        queryFn: () => axiosClient.get("/configuracoes", {
            params: {
                chave: 'config_whatsapp'
            }
        })
    });

    const { mutate: enviarFaturas, isLoading } = useMutation({
        mutationKey: "enviar-faturas",
        mutationFn: (data: TDataEnviarFaturas[]) =>
            axiosClient.post("/financeiro/conta-corrente-consumo/enviar-faturas", {
                faturas: data
            }),
        onSuccess: () => {
            queryClient.invalidateQueries(`detalhar-fechamento-${id}`)
            enqueueSnackbar("Faturas enviadas com sucesso", { variant: 'success' })
            handleClose();
        },
        onError: (error) => {
            console.error("Erro ao enviar o e-mail:", error);
        },
    });

    const dataConfigWhatsapp: TConfigWhatsApp | null = useMemo(() => {
        // Se não vier nada retorna null;
        if (!data?.data) return null;

        // Verificação se possui os campos numero e token
        const isValid = Object.values(data.data).every(
            (item: any) => "numero" in item && "token" in item
        );


        // Se for valido retorna o data
        return isValid ? data.data : null;
    }, [data]);

    //Funções manipuladoras
    const handleEnviar = (data: { faturas: TEnviarFaturas[] }) => {
        if (!validateRequest(data.faturas)) {
            enqueueSnackbar("Verifique os campos não preenchidos", { variant: 'warning' });
            return;
        }

        const params: TDataEnviarFaturas[] = data.faturas.map((fatura, index) => {
            return {
                id: fatura.id,
                telefone: !fatura.telefone ? null : desformatarTelefone(fatura.telefone),
                email: !fatura.email ? null : fatura.email
            }
        })

        enviarFaturas(params)

    };

    const validateRequest = (faturas: TEnviarFaturas[]) => {
        let isValid = true;

        faturas.forEach((fatura, index) => {
            if (fatura.email === "" && fatura.telefone === "") {
                setError(`faturas.${index}.telefone`, {
                    type: 'error'
                })
                setError(`faturas.${index}.email`, {
                    type: 'error'
                })
                isValid = false;
            }
        });
        return isValid;
    }

    return (
        <FormProvider {...methods}>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth='md'
                fullWidth
                scroll="body"
            >
                <DialogTitle>Enviar faturas</DialogTitle>
                <DialogContent>

                    {
                        !dataConfigWhatsapp &&
                        <Alert severity="warning" sx={{ marginBottom: 2 }} >
                            <b>Atenção:</b> Nenhuma configuração de whatsapp encontrada, favor realizar a manutenção do mesmo.
                        </Alert>
                    }

                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell><b>Nome</b></TableCell>
                                    <TableCell sx={{ opacity: !dataConfigWhatsapp ? '0.5' : '1' }}><b>WhatsApp</b></TableCell>
                                    <TableCell><b>E-mail</b></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {
                                    faturas.map((row, index) => {

                                        return (
                                            <TableRow
                                                key={row.id}
                                                sx={{
                                                    '&:last-child td, &:last-child th': {
                                                        border: 0,
                                                    }
                                                }}>
                                                <TableCell>{row.nome}</TableCell>
                                                <TableCell>
                                                    <Controller
                                                        name={`faturas.${index}.telefone`}
                                                        control={control}
                                                        rules={{
                                                            pattern: {
                                                                value: /^\(\d{2}\) \d{4,5}-\d{4}$/,
                                                                message: "Telefone inválido"
                                                            }
                                                        }}
                                                        render={({ field, fieldState: { error } }) => (
                                                            <TextField
                                                                {...field}
                                                                size="small"
                                                                slotProps={{ inputLabel: { shrink: false } }}
                                                                error={!!error}
                                                                onChange={(e) => {
                                                                    const newValue = formatarTelefone(e.target.value)
                                                                    field.onChange(newValue)
                                                                }}
                                                                helperText={error?.message}
                                                                type="tel"
                                                                disabled={!dataConfigWhatsapp}
                                                            />
                                                        )}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Controller
                                                        name={`faturas.${index}.email`}
                                                        control={control}
                                                        rules={{
                                                            pattern: {
                                                                value: /^[\w.-]+@[a-zA-Z\d.-]+\.[a-zA-Z]{2,}$/,
                                                                message: 'E-mail inválido'
                                                            }
                                                        }}
                                                        render={({ field, fieldState: { error } }) => (
                                                            <TextField
                                                                {...field}
                                                                size="small"
                                                                slotProps={{ inputLabel: { shrink: false } }}
                                                                onChange={(e) => field.onChange(e.target.value)}
                                                                error={!!error}
                                                                helperText={error?.message}
                                                            />
                                                        )}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={handleClose} disabled={isLoading}>Fechar</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit(handleEnviar)}
                        disabled={isLoading}
                    >
                        {
                            isLoading &&
                            <CircularProgress size={20} sx={{ marginRight: 1 }} />
                        }
                        Enviar
                    </Button>
                </DialogActions>

            </Dialog>
        </FormProvider>
    )
}


interface DialogEnviarFaturasProps {
    id: number
    open: boolean
    faturas: TFaturaFechamentoData[]
    handleClose: VoidFunction
}

export type TEnviarFaturas = TFaturaFechamentoData & {
    email: string;
    telefone: string;
};

export type TDataEnviarFaturas = {
    id: number;
    telefone: string | null;
    email: string | null;
}

type TConfigWhatsApp = {
    [key: string]: {
        numero: string
        token: string
    }
}